import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Tooltip, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getCityWhitelabelMappings } from "../../../actions/cities";
import {
  clearRewardRule,
  getRewardPrograms,
  getRewardRules,
} from "../../../actions/rewards";
import RewardRuleModal from "../../../components/modal/RewardRuleModal";
import Tag from "../../../components/Tags";
import useCustomTable from "../../../components/table/CustomTable";
import { TAG_COLOR } from "../../../constants/const";
import { useShowToast } from "../../../hooks";
import useFleetsByPaymentConfig from "../../../hooks/useFleetsByPaymentConfig";
import { modifyCurrencyToSymbol } from "../../../utils/utils";

const RewardProgramDetails = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [modalVisible, setModalVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);

  const showToast = useShowToast();
  const dispatch = useDispatch();

  const { fleetCurrencies, getCurrency } = useFleetsByPaymentConfig();

  const rewardPrograms = useSelector((state) => state.rewards.rewardPrograms);
  const rewardRules = useSelector((state) => state.rewards.rewardRules);
  const rewardRulesSuccess = useSelector(
    (state) => state.rewards.rewardRulesSuccess
  );
  const createSuccess = useSelector(
    (state) => state.rewards.createRewardRuleSuccess
  );
  const updateSuccess = useSelector(
    (state) => state.rewards.updateRewardRuleSuccess
  );

  const program = useMemo(() => {
    if (!rewardPrograms || !rewardPrograms.content) return null;
    return rewardPrograms.content.find((p) => p.id === programId);
  }, [rewardPrograms, programId]);

  const currency = program?.organizationPaymentConfigurationId
    ? getCurrency(program.organizationPaymentConfigurationId)
    : null;

  useEffect(() => {
    dispatch(getCityWhitelabelMappings());
    if (!rewardPrograms?.content?.length) {
      dispatch(getRewardPrograms());
    }
  }, [dispatch, rewardPrograms?.content?.length]);

  const formatAmount = (rule) => {
    if (!rule.rewardConfig) return "-";

    const symbol = currency ? modifyCurrencyToSymbol(currency) : "";

    if (rule.rewardConfig.fixed_amount_minor) {
      const amount = (rule.rewardConfig.fixed_amount_minor / 100).toFixed(2);
      return currency ? `${symbol}${amount}` : `${amount} (no currency)`;
    }

    if (rule.rewardConfig.percentage_of_trigger) {
      const maxAmount = rule.rewardConfig.max_amount_minor
        ? ` (max ${symbol}${(rule.rewardConfig.max_amount_minor / 100).toFixed(2)})`
        : "";
      return `${rule.rewardConfig.percentage_of_trigger}%${maxAmount}`;
    }

    return "-";
  };

  const columns = useMemo(
    () => [
      {
        name: "Name",
        selector: "name",
        sortable: true,
        center: true,
      },
      {
        name: "Trigger Type",
        selector: "triggerType",
        sortable: true,
        center: true,
      },
      {
        name: "Reward Type",
        selector: "rewardType",
        sortable: true,
        center: true,
      },
      {
        name: "Amount",
        selector: "rewardConfig",
        sortable: false,
        center: true,
        cell: (row) => formatAmount(row),
      },
      {
        name: "Per User Limit",
        selector: "perUserLimit",
        sortable: true,
        center: true,
        cell: (row) => row.perUserLimit || "-",
      },
      {
        name: "Priority",
        selector: "priority",
        sortable: true,
        center: true,
        cell: (row) => row.priority || 0,
      },
      {
        name: "Actions",
        center: true,
        cell: (row) => (
          <Tooltip title="Edit Rule" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                setSelectedRule(row);
                setIsEdit(true);
                setModalVisible(true);
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [currency]
  );

  const addButtonClick = () => {
    setSelectedRule(null);
    setIsEdit(false);
    setModalVisible(true);
  };

  const {
    renderCustomTable,
    renderHeaderBar,
    localPage,
    localRows,
    sortData,
    debouncedSearchTerm,
    filterData,
    setLoading,
    resetPagination,
    searchFilter,
  } = useCustomTable(
    columns,
    rewardRules,
    null,
    addButtonClick,
    [],
    "Reward Rules",
    true,
    false
  );

  const reloadData = useCallback(() => {
    setLoading(true);
    dispatch(
      getRewardRules({
        filter: {
          reward_program_id: programId,
        },
      })
    );
  }, [dispatch, programId, setLoading]);

  useEffect(() => {
    if (rewardRulesSuccess) {
      setLoading(false);
    }
  }, [rewardRulesSuccess]);

  useEffect(() => {
    reloadData();
  }, [reloadData]);

  const handleClose = () => {
    setModalVisible(false);
    setIsEdit(false);
    setSelectedRule(null);
  };

  useEffect(() => {
    if (createSuccess === true) {
      showToast("Reward Rule Created", "success");
      reloadData();
      dispatch(clearRewardRule());
      setModalVisible(false);
    }
  }, [createSuccess]);

  useEffect(() => {
    if (updateSuccess === true) {
      showToast("Reward Rule Updated", "success");
      reloadData();
      dispatch(clearRewardRule());
      setModalVisible(false);
    }
  }, [updateSuccess]);

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton
            onClick={() => navigate("/dashboard/promotions/rewards")}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {program?.name || "Loading..."}
          </Typography>
        </Box>
        {program && (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Tag
              text={program.isActive ? "Active" : "Inactive"}
              colorTag={program.isActive ? TAG_COLOR.GREEN : TAG_COLOR.RED}
            />
            {program.startDate && (
              <Typography variant="body2" color="text.secondary">
                Start: {new Date(program.startDate).toLocaleDateString()}
              </Typography>
            )}
            {program.endDate && (
              <Typography variant="body2" color="text.secondary">
                End: {new Date(program.endDate).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {renderHeaderBar()}
      {renderCustomTable()}

      <RewardRuleModal
        visible={modalVisible}
        onClose={handleClose}
        isEdit={isEdit}
        ruleData={selectedRule}
        programId={programId}
        currency={currency}
      />
    </>
  );
};

export default RewardProgramDetails;
